import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, RefreshCw, Inbox, StickyNote, Trash2, Mail, MessageCircle, Download, Paperclip, FileDown } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_LABELS = {
  "3d-print-build": "3D Print / Build",
  "web-launch": "Web & Launch",
  "media-assets": "Media Assets",
  "product-launch": "Product Launch",
  marketing: "Marketing",
  "brand-assets": "Brand Assets",
};

const STATUSES = ["new", "contacted", "quoted", "won"];
const STATUS_STYLES = {
  new: "bg-[#45f3ff]/10 text-[#45f3ff] border-[#45f3ff]/40",
  contacted: "bg-[#6c72cb]/20 text-[#a5a8ff] border-[#6c72cb]/40",
  quoted: "bg-amber-400/10 text-amber-300 border-amber-400/40",
  won: "bg-emerald-400/10 text-emerald-300 border-emerald-400/40",
};

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const phoneDigits = (contact) => {
  const digits = contact.replace(/[^\d+]/g, "");
  return digits.replace(/\D/g, "").length >= 8 ? digits.replace(/\D/g, "") : null;
};

const whatsappHref = (q) => {
  const num = phoneDigits(q.contact);
  return num
    ? `https://wa.me/${num}?text=${encodeURIComponent(
        `Hi ${q.name}, thanks for your enquiry with GOAT Productions. `
      )}`
    : null;
};

const REPLY_TEMPLATES = [
  {
    id: "quote-sent",
    label: "Quote Sent",
    subject: (q) => `Your GOAT Productions quote — ${q.name}`,
    body: (q) =>
      `Hi ${q.name},\n\nThanks for your enquiry. Please find your quote below.\n\n[Scope: ${(q.services || [])
        .map((s) => SERVICE_LABELS[s] || s)
        .join(", ") || "as discussed"}]\n\n[Price and timeline here]\n\nReply with any questions — happy to adjust.\n\nGOAT Productions`,
  },
  {
    id: "follow-up",
    label: "Follow-up",
    subject: (q) => `Following up on your GOAT Productions enquiry — ${q.name}`,
    body: (q) =>
      `Hi ${q.name},\n\nJust checking in on your enquiry — are you still looking to move ahead? Happy to answer questions or adjust the scope.\n\nGOAT Productions`,
  },
  {
    id: "thank-you",
    label: "Thank You",
    subject: (q) => `Great working with you, ${q.name}`,
    body: (q) =>
      `Hi ${q.name},\n\nThank you for choosing GOAT Productions — it's been a pleasure bringing your project to life. If anything else comes up, you know where to find us.\n\nGOAT Productions`,
  },
  {
    id: "blank",
    label: "Blank",
    subject: (q) => `Re: Your GOAT Productions enquiry — ${q.name}`,
    body: (q) => `Hi ${q.name},\n\nThanks for your enquiry with GOAT Productions.\n\n`,
  },
];

const buildMailto = (q, tmpl) =>
  `mailto:${q.contact}?subject=${encodeURIComponent(tmpl.subject(q))}&body=${encodeURIComponent(
    tmpl.body(q)
  )}`;

const buildWhatsapp = (q, tmpl) => {
  const num = phoneDigits(q.contact);
  return num ? `https://wa.me/${num}?text=${encodeURIComponent(tmpl.body(q))}` : null;
};

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [noteDrafts, setNoteDrafts] = useState({});
  const [wonPrompt, setWonPrompt] = useState(null);
  const [wonValue, setWonValue] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [replyPicker, setReplyPicker] = useState(null);

  const deleteEnquiry = async (id) => {
    try {
      await axios.delete(`${API}/quotes/${id}`, { withCredentials: true });
      setQuotes((prev) => prev.filter((q) => q.id !== id));
      setDeleteConfirm(null);
      toast.success("Enquiry deleted");
    } catch (err) {
      toast.error("Could not delete enquiry.");
    }
  };

  const handleReply = (q) => {
    if ((q.status || "new") === "new") {
      setStatus(q.id, "contacted");
    }
  };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Name", "Contact", "Type", "Status", "Deal Value", "Services", "Message", "Notes", "Received"];
    const rows = quotes.map((q) => [
      q.name,
      q.contact,
      q.request_type,
      q.status || "new",
      q.deal_value ?? "",
      (q.services || []).map((s) => SERVICE_LABELS[s] || s).join("; "),
      q.message || "",
      (q.notes || []).map((n) => n.text).join(" | "),
      new Date(q.created_at).toISOString(),
    ]);
    const csv = [header, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `goat-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${quotes.length} enquiries`);
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const resp = await axios.get(`${API}/files/${fileId}/download`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Could not download file.");
    }
  };

  const attachFile = async (id, file) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/quotes/${id}/files`, formData, {
        withCredentials: true,
      });
      setQuotes((prev) => prev.map((q) => (q.id === id ? data : q)));
      toast.success("File attached");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not attach file.");
    }
  };

  const fmtSize = (bytes) =>
    bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)}MB` : `${Math.ceil(bytes / 1024)}KB`;

  const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;
  const isOverdue = (q) =>
    (q.status || "new") === "new" &&
    Date.now() - new Date(q.created_at).getTime() > THREE_DAYS_MS;

  const wonTotal = quotes
    .filter((q) => q.status === "won" && q.deal_value)
    .reduce((sum, q) => sum + q.deal_value, 0);

  const addNote = async (id) => {
    const text = (noteDrafts[id] || "").trim();
    if (!text) return;
    try {
      const { data } = await axios.post(
        `${API}/quotes/${id}/notes`,
        { text },
        { withCredentials: true }
      );
      setQuotes((prev) => prev.map((q) => (q.id === id ? data : q)));
      setNoteDrafts((prev) => ({ ...prev, [id]: "" }));
      toast.success("Note added");
    } catch (err) {
      toast.error("Could not save note.");
    }
  };

  const setStatus = async (id, status, dealValue = null) => {
    if (status === "won" && dealValue === null) {
      setWonPrompt(id);
      setWonValue("");
      return;
    }
    try {
      const payload = { status };
      if (status === "won" && dealValue !== null && dealValue !== "") {
        payload.deal_value = Number(dealValue);
      }
      const { data } = await axios.patch(`${API}/quotes/${id}/status`, payload, {
        withCredentials: true,
      });
      setQuotes((prev) => prev.map((q) => (q.id === id ? data : q)));
      setWonPrompt(null);
      setWonValue("");
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error("Could not update status.");
    }
  };

  const visible = filter === "all" ? quotes : quotes.filter((q) => (q.status || "new") === filter);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/quotes`, { withCredentials: true });
      setQuotes(data);
    } catch (err) {
      setError("Could not load enquiries. Try refreshing.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0b0c10] text-[#e0e2ec]">
      <header className="border-b border-[#24283b] bg-[#0b0c10]/90 px-6 lg:px-12 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/goat-mark.jpg" alt="GOAT Productions" className="h-8" />
          <div className="flex flex-col">
            <span className="font-display text-lg tracking-wider uppercase text-white leading-none">
              Enquiry Inbox
            </span>
            <span className="font-mono-goat text-[9px] uppercase tracking-widest text-[#45f3ff]">
              {user?.email}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span
            data-testid="won-revenue-total"
            className="font-mono-goat text-xs text-emerald-300 border border-emerald-400/40 bg-emerald-400/10 px-3 py-2 uppercase tracking-wider"
          >
            Won: ${wonTotal.toLocaleString()}
          </span>
          <button
            onClick={exportCsv}
            data-testid="export-csv-btn"
            className="inline-flex items-center gap-2 border border-[#24283b] px-4 py-2 font-mono-goat text-xs uppercase tracking-wider text-neutral-300 hover:border-[#45f3ff] transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button
            onClick={load}
            data-testid="admin-refresh-btn"
            className="inline-flex items-center gap-2 border border-[#24283b] px-4 py-2 font-mono-goat text-xs uppercase tracking-wider text-neutral-300 hover:border-[#45f3ff] transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 bg-[#45f3ff] text-black px-4 py-2 font-mono-goat font-bold text-xs uppercase tracking-wider hover:bg-white transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-10" data-testid="admin-dashboard">
        {loading && (
          <p className="font-mono-goat text-xs text-neutral-400">Loading enquiries…</p>
        )}
        {error && (
          <p data-testid="admin-error" className="font-mono-goat text-xs text-red-400">
            {error}
          </p>
        )}
        {!loading && !error && quotes.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="pipeline-filters">
            {["all", ...STATUSES].map((s) => (
              <button
                key={s}
                data-testid={`filter-${s}`}
                onClick={() => setFilter(s)}
                className={`px-4 py-2 font-mono-goat text-xs uppercase tracking-wider border transition-colors ${
                  filter === s
                    ? "bg-[#45f3ff] text-black border-[#45f3ff] font-bold"
                    : "bg-transparent text-neutral-400 border-[#24283b] hover:border-[#45f3ff]"
                }`}
              >
                {s} ({s === "all" ? quotes.length : quotes.filter((q) => (q.status || "new") === s).length})
              </button>
            ))}
          </div>
        )}

        {!loading && !error && quotes.length === 0 && (
          <div
            data-testid="admin-empty-state"
            className="border border-dashed border-[#24283b] bg-[#101420] p-12 text-center"
          >
            <Inbox className="w-8 h-8 text-[#45f3ff] mx-auto mb-3" />
            <p className="font-mono-goat text-xs text-neutral-400">
              No enquiries yet. New quote and consultation requests will appear here.
            </p>
          </div>
        )}

        <div className="space-y-4">
          {visible.map((q) => (
            <article
              key={q.id}
              data-testid={`enquiry-card-${q.id}`}
              className="border border-[#24283b] bg-[#101420] p-6 hover:border-[#45f3ff] transition-colors"
            >
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <span
                    className={`font-mono-goat text-[10px] uppercase tracking-widest px-2 py-1 ${
                      q.request_type === "consultation"
                        ? "bg-[#6c72cb]/20 text-[#a5a8ff] border border-[#6c72cb]/40"
                        : "bg-[#45f3ff]/10 text-[#45f3ff] border border-[#45f3ff]/40"
                    }`}
                  >
                    {q.request_type === "consultation" ? "Consultation" : "Quote"}
                  </span>
                  <span
                    data-testid={`status-badge-${q.id}`}
                    className={`font-mono-goat text-[10px] uppercase tracking-widest px-2 py-1 border ${STATUS_STYLES[q.status || "new"]}`}
                  >
                    {q.status || "new"}
                  </span>
                  {isOverdue(q) && (
                    <span
                      data-testid={`overdue-badge-${q.id}`}
                      className="font-mono-goat text-[10px] uppercase tracking-widest px-2 py-1 border border-amber-400/50 bg-amber-400/10 text-amber-300 animate-pulse"
                    >
                      Follow-up overdue
                    </span>
                  )}
                  {q.status === "won" && q.deal_value != null && (
                    <span className="font-mono-goat text-[10px] uppercase tracking-widest px-2 py-1 border border-emerald-400/40 bg-emerald-400/10 text-emerald-300">
                      ${q.deal_value.toLocaleString()}
                    </span>
                  )}
                  <h2 className="font-display text-xl uppercase tracking-wide text-white">
                    {q.name}
                  </h2>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono-goat text-[10px] text-neutral-500">
                    {new Date(q.created_at).toLocaleString()}
                  </span>
                  {deleteConfirm === q.id ? (
                    <span className="flex items-center gap-2" data-testid={`delete-confirm-${q.id}`}>
                      <button
                        data-testid={`delete-confirm-yes-${q.id}`}
                        onClick={() => deleteEnquiry(q.id)}
                        className="px-3 py-1.5 bg-red-500 text-black font-mono-goat text-[10px] font-bold uppercase tracking-wider hover:bg-white transition-colors"
                      >
                        Confirm Delete
                      </button>
                      <button
                        data-testid={`delete-confirm-no-${q.id}`}
                        onClick={() => setDeleteConfirm(null)}
                        className="px-3 py-1.5 font-mono-goat text-[10px] uppercase tracking-wider text-neutral-400 hover:text-white transition-colors"
                      >
                        Keep
                      </button>
                    </span>
                  ) : (
                    <button
                      data-testid={`delete-btn-${q.id}`}
                      onClick={() => setDeleteConfirm(q.id)}
                      title="Delete enquiry"
                      className="p-1.5 border border-[#24283b] text-neutral-500 hover:border-red-400 hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <p className="font-mono-goat text-xs text-neutral-300 mb-2 flex items-center gap-2">
                Contact: <span className="text-[#45f3ff]">{q.contact}</span>
                {(EMAIL_RE.test(q.contact) || whatsappHref(q)) && (
                  <button
                    onClick={() => setReplyPicker(replyPicker === q.id ? null : q.id)}
                    data-testid={`reply-btn-${q.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-[#24283b] font-mono-goat text-[10px] uppercase tracking-wider text-[#45f3ff] hover:border-[#45f3ff] hover:bg-[#45f3ff]/10 transition-colors"
                  >
                    {EMAIL_RE.test(q.contact) ? (
                      <Mail className="w-3 h-3" />
                    ) : (
                      <MessageCircle className="w-3 h-3" />
                    )}
                    Reply
                  </button>
                )}
              </p>

              {replyPicker === q.id && (
                <div
                  className="flex flex-wrap gap-2 mb-3 p-3 border border-[#45f3ff]/30 bg-[#0e121c]"
                  data-testid={`reply-templates-${q.id}`}
                >
                  <span className="w-full font-mono-goat text-[10px] uppercase tracking-widest text-neutral-400">
                    Choose a template
                  </span>
                  {REPLY_TEMPLATES.map((t) => {
                    const href = EMAIL_RE.test(q.contact)
                      ? buildMailto(q, t)
                      : buildWhatsapp(q, t);
                    return (
                      <a
                        key={t.id}
                        href={href}
                        target={EMAIL_RE.test(q.contact) ? undefined : "_blank"}
                        rel="noopener noreferrer"
                        onClick={() => {
                          handleReply(q);
                          setReplyPicker(null);
                        }}
                        data-testid={`reply-template-${t.id}-${q.id}`}
                        className="px-3 py-1.5 border border-[#24283b] font-mono-goat text-[10px] uppercase tracking-wider text-neutral-300 hover:border-[#45f3ff] hover:text-white transition-colors"
                      >
                        {t.label}
                      </a>
                    );
                  })}
                  <button
                    onClick={() => setReplyPicker(null)}
                    data-testid={`reply-picker-close-${q.id}`}
                    className="px-3 py-1.5 font-mono-goat text-[10px] uppercase tracking-wider text-neutral-500 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}
              {q.services?.length > 0 && (
                <p className="font-mono-goat text-xs text-neutral-400 mb-2">
                  Scope: {q.services.map((s) => SERVICE_LABELS[s] || s).join(" • ")}
                </p>
              )}
              {q.message && (
                <p className="text-sm text-neutral-400 mt-3 border-l-2 border-[#24283b] pl-3">
                  {q.message}
                </p>
              )}
              <div className="flex flex-wrap gap-2 mt-4" data-testid={`status-actions-${q.id}`}>
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    data-testid={`set-status-${s}-${q.id}`}
                    onClick={() => setStatus(q.id, s)}
                    disabled={(q.status || "new") === s}
                    className={`px-3 py-1.5 font-mono-goat text-[10px] uppercase tracking-wider border transition-colors disabled:opacity-40 disabled:cursor-default ${
                      (q.status || "new") === s
                        ? "border-[#45f3ff] text-[#45f3ff]"
                        : "border-[#24283b] text-neutral-400 hover:border-[#45f3ff] hover:text-white"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>

              {wonPrompt === q.id && (
                <div
                  className="flex flex-wrap items-center gap-2 mt-3 p-3 border border-emerald-400/40 bg-emerald-400/5"
                  data-testid={`won-value-form-${q.id}`}
                >
                  <span className="font-mono-goat text-[10px] uppercase tracking-widest text-emerald-300">
                    Deal value (optional)
                  </span>
                  <input
                    type="number"
                    min="0"
                    data-testid={`won-value-input-${q.id}`}
                    value={wonValue}
                    onChange={(e) => setWonValue(e.target.value)}
                    placeholder="e.g. 4000"
                    className="w-32 bg-[#161a29] border border-[#24283b] text-white px-3 py-2 text-xs focus:border-emerald-400 focus:outline-none transition-colors"
                  />
                  <button
                    data-testid={`won-value-confirm-${q.id}`}
                    onClick={() => setStatus(q.id, "won", wonValue)}
                    className="px-4 py-2 bg-emerald-400 text-black font-mono-goat text-[10px] font-bold uppercase tracking-wider hover:bg-white transition-colors"
                  >
                    Confirm Won
                  </button>
                  <button
                    data-testid={`won-value-cancel-${q.id}`}
                    onClick={() => setWonPrompt(null)}
                    className="px-3 py-2 font-mono-goat text-[10px] uppercase tracking-wider text-neutral-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}

              <div className="mt-4 border-t border-[#24283b] pt-4" data-testid={`attachments-section-${q.id}`}>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  {(q.attachments || []).map((a) => (
                    <button
                      key={a.id}
                      data-testid={`attachment-${a.id}`}
                      onClick={() => downloadFile(a.id, a.filename)}
                      title={`${a.filename} (${fmtSize(a.size)})`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono-goat text-[10px] uppercase tracking-wider transition-colors ${
                        a.source === "client"
                          ? "border-[#45f3ff]/40 text-[#45f3ff] hover:bg-[#45f3ff]/10"
                          : "border-[#6c72cb]/40 text-[#a5a8ff] hover:bg-[#6c72cb]/10"
                      }`}
                    >
                      <FileDown className="w-3 h-3" /> {a.filename}
                    </button>
                  ))}
                  <label
                    data-testid={`attach-btn-${q.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-[#24283b] font-mono-goat text-[10px] uppercase tracking-wider text-neutral-400 cursor-pointer hover:border-[#45f3ff] hover:text-white transition-colors"
                  >
                    <Paperclip className="w-3 h-3" /> Attach file
                    <input
                      type="file"
                      className="hidden"
                      data-testid={`attach-input-${q.id}`}
                      onChange={(e) => {
                        attachFile(q.id, e.target.files?.[0]);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              </div>

              <div className="mt-4 border-t border-[#24283b] pt-4" data-testid={`notes-section-${q.id}`}>
                {(q.notes || []).map((n) => (
                  <div key={n.id} className="flex gap-2 mb-2 items-start">
                    <StickyNote className="w-3.5 h-3.5 text-[#6c72cb] mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-neutral-300">{n.text}</p>
                      <span className="font-mono-goat text-[9px] text-neutral-500">
                        {new Date(n.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
                <div className="flex gap-2 mt-2">
                  <input
                    type="text"
                    data-testid={`note-input-${q.id}`}
                    value={noteDrafts[q.id] || ""}
                    onChange={(e) =>
                      setNoteDrafts((prev) => ({ ...prev, [q.id]: e.target.value }))
                    }
                    onKeyDown={(e) => e.key === "Enter" && addNote(q.id)}
                    placeholder="Add a private note (quote details, call outcome…)"
                    className="flex-1 bg-[#161a29] border border-[#24283b] text-white px-3 py-2 text-xs focus:border-[#45f3ff] focus:outline-none transition-colors"
                  />
                  <button
                    data-testid={`note-add-${q.id}`}
                    onClick={() => addNote(q.id)}
                    className="px-4 py-2 border border-[#24283b] font-mono-goat text-[10px] uppercase tracking-wider text-neutral-300 hover:border-[#45f3ff] hover:text-white transition-colors"
                  >
                    Add Note
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10">
          <Link
            to="/"
            data-testid="admin-back-home-link"
            className="font-mono-goat text-xs text-neutral-500 hover:text-[#45f3ff] transition-colors"
          >
            ← Back to site
          </Link>
        </div>
      </main>
    </div>
  );
}
